// AutomationExecutionGrid.tsx - Enhanced with Better Design & Pagination

import React, { useEffect, useState } from 'react';
import { Table, Tag, Tooltip, Spin, Card } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { getAutomationById } from '../../services/automationService';
import './AutomationExecutionGrid.css';
import logger from '../../utils/logger';

interface ExecutionData {
  key: string;
  runDate: string;
  actionName: string;
  status: 'completed' | 'failed' | 'pending';
  messageSent: string;
  leadName: string;
  step: string;
  leadId?: string;
  actionType?: string;
  error?: any[];
}

interface AutomationExecutionGridProps {
  automationId: string;
  isVisible?: boolean; // Made optional with default
}

const AutomationExecutionGrid: React.FC<AutomationExecutionGridProps> = ({
  automationId,
  isVisible = true // Default value
}) => {
  // State management
  const [data, setData] = useState<ExecutionData[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0); // Add total count from API
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // Helper functions
  const getActionDisplayName = (actionType: string): string => {
    const actionMap: { [key: string]: string } = {
      'send_whatsapp': 'WhatsApp Message',
      'send_email': 'Email',
      'send_gmail': 'Gmail',
      'send_zeptomail': 'ZeptoMail',
      'create_task': 'Create Task',
      'status_change': 'Status Change',
      'label_change': 'Label Change',
      'assign_lead': 'Assign Lead',
      'push_notification': 'Notification',
      'move_to_list': 'Move to List',
      'copy_to_list': 'Copy to List',
      'add_to_google_sheet': 'Google Sheets',
      'add_facebook_audience': 'Facebook Audience',
      'delay': 'Delay'
    };
    return actionMap[actionType] || actionType;
  };

  const getMessageSentStatus = (execution: any): string => {
    if (execution.whatsappMessageTracker?.messageStatus) {
      return execution.whatsappMessageTracker.messageStatus;
    }
    return execution.status === 'completed' ? 'Success' : 'Failed';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return '#52c41a';
      case 'failed':
        return '#ff4d4f';
      default:
        return '#faad14';
    }
  };

  // Fetch execution data with pagination
  const fetchExecutionData = async (page: number = currentPage, size: number = pageSize) => {
    if (!automationId) return;
    
    try {
      setLoading(true);
      logger.log(`Fetching execution data for automation: ${automationId}, page: ${page}, size: ${size}`);
      
      const response = await getAutomationById(automationId, {
        page: page,
        perPage: size,
        orderBy: "scheduleAt", // Order by date
        isAscending: false // Latest first
      });
      
      logger.log('Full response:', response);
      logger.log('Response.data:', response.data);
      
      if (response?.status && response?.data) {
        const executions = response.data.data?.captureEventActions || [];
        const totalCount = response.data.total || response.data.data?.total || executions.length;
        
        logger.log('Raw executions found:', executions.length);
        logger.log('Total count from API:', totalCount);
        
        const formattedData = executions.map((execution: any, index: number) => ({
          key: execution._id || `execution-${index}`,
          runDate: execution.scheduleAt || new Date().toISOString(),
          actionName: getActionDisplayName(execution.actionType || 'unknown'),
          status: execution.status || 'pending',
          messageSent: getMessageSentStatus(execution),
          leadName: execution.lead?.name || 'Unknown Lead',
          step: execution.step || 'N/A',
          leadId: execution.lead?._id,
          actionType: execution.actionType || 'unknown',
          error: execution.error || []
        }));

        logger.log('Setting formatted data:', formattedData);
        setData(formattedData);
        setTotal(totalCount);
      }
    } catch (error) {
      console.error('Error fetching execution data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: (
        <div className="column-header">
          <CalendarOutlined className="column-icon" />
          <span>Run Date</span>
        </div>
      ),
      dataIndex: 'runDate',
      key: 'runDate',
      width: 180,
      sorter: (a: ExecutionData, b: ExecutionData) => 
        moment(a.runDate).valueOf() - moment(b.runDate).valueOf(),
      defaultSortOrder: 'descend' as const,
      render: (date: string) => (
        <div className="date-cell">
          <div className="date-primary">
            {moment(date).format('MMM DD, YYYY')}
          </div>
          <div className="date-secondary">
            {moment(date).format('h:mm A')}
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className="column-header">
          <UserOutlined className="column-icon" />
          <span>Lead</span>
        </div>
      ),
      dataIndex: 'leadName',
      key: 'leadName',
      width: 200,
      render: (name: string, record: ExecutionData) => (
        <div className="lead-cell">
          <div className="lead-name">{name}</div>
          <div className="lead-step">{record.step}</div>
        </div>
      ),
    },
    {
      title: (
        <div className="column-header">
          <PlayCircleOutlined className="column-icon" />
          <span>Action</span>
        </div>
      ),
      dataIndex: 'actionName',
      key: 'actionName',
      width: 180,
      render: (actionName: string) => (
        <div className="action-cell">
          <Tag className="action-tag">{actionName}</Tag>
        </div>
      ),
    },
    {
      title: (
        <div className="column-header">
          <span>Status</span>
        </div>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <div className="status-cell">
          {getStatusIcon(status)}
          <span 
            className="status-text"
            style={{ color: getStatusColor(status) }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        </div>
      ),
    },
    {
      title: (
        <div className="column-header">
          <span>Message Status</span>
        </div>
      ),
      dataIndex: 'messageSent',
      key: 'messageSent',
      width: 140,
      render: (status: string) => (
        <Tag 
          color={status === 'delivered' ? 'green' : 
                status === 'failed' ? 'red' : 
                status === 'sent' ? 'blue' : 'orange'}
          className="message-status-tag"
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: (
        <div className="column-header">
          <span>Actions</span>
        </div>
      ),
      key: 'actions',
      width: 100,
      render: (record: ExecutionData) => (
        <div className="actions-cell">
          {record.error && record.error.length > 0 && (
            <Tooltip 
              title={
                <div>
                  <strong>Error Details:</strong><br/>
                  {JSON.stringify(record.error, null, 2)}
                </div>
              }
              overlayClassName="error-tooltip"
            >
              <Tag color="red" className="error-tag">
                View Error
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  // Handle pagination change
  const handlePaginationChange = (page: number, size?: number) => {
    logger.log(`Pagination changed: page=${page}, size=${size}`);
    const newPageSize = size || pageSize;
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
    // Fetch new data for the selected page
    fetchExecutionData(page, newPageSize);
  };

  // Effects
  useEffect(() => {
    if (isVisible && automationId) {
      fetchExecutionData();
    }
  }, [isVisible, automationId]);

  if (!isVisible) return null;

  return (
    <div className="execution-grid-container">
      <Card className="execution-card">
        {/* Header */}
        <div className="execution-header">
          <div className="header-title">
            <PlayCircleOutlined className="header-icon" />
            <span>Execution History</span>
            <span className="execution-count">({total} total records)</span>
          </div>
        </div>

        {/* Table */}
        <div className="execution-table-container">
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total, // Use total from API
              onChange: handlePaginationChange,
              showSizeChanger: true,
              showQuickJumper: false, // Simplified
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} executions`,
              pageSizeOptions: ['15', '25', '50', '100'], // Match API defaults
              className: 'execution-pagination',
              position: ['bottomCenter'], // Center the pagination
              size: 'default'
            }}
            scroll={{ x: 1000, y: 'calc(100vh - 400px)' }} // Added vertical scroll
            className="execution-table"
            size="middle"
            locale={{
              emptyText: (
                <div className="empty-state">
                  <PlayCircleOutlined className="empty-icon" />
                  <div className="empty-title">No Executions Found</div>
                  <div className="empty-description">
                    This automation hasn't run yet.
                  </div>
                </div>
              )
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default AutomationExecutionGrid;